import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { authService } from '../services/auth.service';
import './Utilisateurs.css';

interface Utilisateur {
  id: number;
  email: string;
  nom: string;
  prenom: string;
  role: string;
  actif: boolean;
  googleAutorise: boolean;
  boutiqueId: number | null;
  boutiqueNom: string | null;
  createdAt: string;
}

interface Boutique {
  id: number;
  nom: string;
  ville: string;
}

const userVide = {
  email: '',
  password: '',
  nom: '',
  prenom: '',
  role: 'vendeur',
  actif: true,
  googleAutorise: false,
  boutiqueId: null as number | null,
  boutiqueNom: null as string | null,
};

const roleBadge = (role: string) => {
  const styles: any = {
    admin: { bg: '#FCEBEB', color: '#A32D2D', label: '👑 Admin' },
    gerant: { bg: '#E6F1FB', color: '#185FA5', label: '🏪 Gérant' },
    vendeur: { bg: '#EAF3DE', color: '#3B6D11', label: '🛒 Vendeur' },
  };
  const s = styles[role] || styles.vendeur;
  return (
    <span style={{
      background: s.bg, color: s.color,
      padding: '3px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600
    }}>{s.label}</span>
  );
};

export default function Utilisateurs() {
  const currentUser = authService.getUser();
  const [utilisateurs, setUtilisateurs] = useState<Utilisateur[]>([]);
  const [boutiques, setBoutiques] = useState<Boutique[]>([]);
  const [chargement, setChargement] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [enEdition, setEnEdition] = useState<number | null>(null);
  const [userSelectionne, setUserSelectionne] = useState<Utilisateur | null>(null);
  const [form, setForm] = useState(userVide);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');

  useEffect(() => {
    chargerDonnees();
  }, []);

  const chargerDonnees = async () => {
    try {
      const [usersRes, boutiquesRes] = await Promise.all([
        api.get('/users'),
        api.get('/boutiques'),
      ]);
      setUtilisateurs(usersRes.data);
      setBoutiques(boutiquesRes.data);
    } catch (e) {
      setErreur('Impossible de charger les données');
    } finally {
      setChargement(false);
    }
  };

  const ouvrirCreation = () => {
    setEnEdition(null);
    setForm(userVide);
    setErreur('');
    setShowModal(true);
  };

  const ouvrirEdition = (u: Utilisateur) => {
    setEnEdition(u.id);
    setForm({
      email: u.email,
      password: '',
      nom: u.nom,
      prenom: u.prenom,
      role: u.role,
      actif: u.actif,
      googleAutorise: u.googleAutorise,
      boutiqueId: u.boutiqueId,
      boutiqueNom: u.boutiqueNom,
    });
    setErreur('');
    setShowModal(true);
  };

  const sauvegarder = async () => {
    if (!form.email || !form.nom || !form.prenom) {
      setErreur('Email, nom et prénom sont obligatoires');
      return;
    }
    if (!enEdition && !form.password) {
      setErreur('Le mot de passe est obligatoire pour un nouvel utilisateur');
      return;
    }
    if (form.role === 'vendeur' && !form.boutiqueId) {
      setErreur('Un vendeur doit être assigné à une boutique');
      return;
    }
    try {
      if (enEdition) {
        const data: any = {
          nom: form.nom,
          prenom: form.prenom,
          role: form.role,
          actif: form.actif,
          googleAutorise: form.googleAutorise,
          boutiqueId: form.boutiqueId,
          boutiqueNom: form.boutiqueNom,
        };
        await api.put(`/users/${enEdition}`, data);
        setSucces('Utilisateur modifié avec succès');
      } else {
        await api.post('/users', form);
        setSucces('Utilisateur créé avec succès');
      }
      setShowModal(false);
      chargerDonnees();
      setTimeout(() => setSucces(''), 3000);
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Une erreur est survenue');
    }
  };

  const supprimer = async () => {
    if (!userSelectionne) return;
    try {
      await api.delete(`/users/${userSelectionne.id}`);
      setSucces('Utilisateur supprimé');
      setShowDeleteModal(false);
      chargerDonnees();
      setTimeout(() => setSucces(''), 3000);
    } catch (e) {
      setErreur('Erreur lors de la suppression');
    }
  };

  const toggleActif = async (u: Utilisateur) => {
    try {
      await api.put(`/users/${u.id}`, { actif: !u.actif });
      chargerDonnees();
      setSucces(`Compte ${!u.actif ? 'activé' : 'désactivé'}`);
      setTimeout(() => setSucces(''), 3000);
    } catch (e) {
      setErreur('Erreur lors de la mise à jour');
    }
  };

  const changerBoutique = (boutiqueId: number | null) => {
    if (!boutiqueId) {
      setForm(f => ({ ...f, boutiqueId: null, boutiqueNom: null }));
      return;
    }
    const boutique = boutiques.find(b => b.id === boutiqueId);
    setForm(f => ({
      ...f,
      boutiqueId,
      boutiqueNom: boutique?.nom || null,
    }));
  };

  const stats = {
    total: utilisateurs.length,
    actifs: utilisateurs.filter(u => u.actif).length,
    admins: utilisateurs.filter(u => u.role === 'admin').length,
    gerants: utilisateurs.filter(u => u.role === 'gerant').length,
    vendeurs: utilisateurs.filter(u => u.role === 'vendeur').length,
    sansboutique: utilisateurs.filter(u => u.role === 'vendeur' && !u.boutiqueId).length,
  };

  if (chargement) return <div className="page-chargement">Chargement...</div>;

  return (
    <div className="utilisateurs-page">
      <div className="page-header">
        <div>
          <h1>Utilisateurs</h1>
          <p>Gérez les comptes, les accès et les boutiques assignées</p>
        </div>
        <button className="btn-primary" onClick={ouvrirCreation}>
          + Nouvel utilisateur
        </button>
      </div>

      {succes && <div className="alert-succes">✅ {succes}</div>}
      {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

      {/* ALERTE VENDEURS SANS BOUTIQUE */}
      {stats.sansboutique > 0 && (
        <div className="alert-warning">
          ⚠️ {stats.sansboutique} vendeur{stats.sansboutique > 1 ? 's' : ''} sans boutique assignée —
          assignez-leur une boutique pour restreindre leurs accès
        </div>
      )}

      {/* STATS */}
      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-val">{stats.total}</span>
          <span className="stat-lbl">Total</span>
        </div>
        <div className="stat-item">
          <span className="stat-val" style={{ color: '#3B6D11' }}>{stats.actifs}</span>
          <span className="stat-lbl">Actifs</span>
        </div>
        <div className="stat-item">
          <span className="stat-val" style={{ color: '#A32D2D' }}>{stats.admins}</span>
          <span className="stat-lbl">Admins</span>
        </div>
        <div className="stat-item">
          <span className="stat-val" style={{ color: '#185FA5' }}>{stats.gerants}</span>
          <span className="stat-lbl">Gérants</span>
        </div>
        <div className="stat-item">
          <span className="stat-val" style={{ color: '#3B6D11' }}>{stats.vendeurs}</span>
          <span className="stat-lbl">Vendeurs</span>
        </div>
        <div className="stat-item">
          <span className="stat-val" style={{ color: stats.sansboutique > 0 ? '#A32D2D' : '#3B6D11' }}>
            {stats.sansboutique}
          </span>
          <span className="stat-lbl">Sans boutique</span>
        </div>
      </div>

      {/* TABLEAU */}
      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Utilisateur</th>
              <th>Email</th>
              <th>Rôle</th>
              <th>Boutique assignée</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {utilisateurs.map(u => (
              <tr key={u.id} className={!u.actif ? 'row-inactif' : ''}>
                <td>
                  <div className="user-cell">
                    <div className="user-avatar-sm" style={{
                      background: u.role === 'admin' ? '#FCEBEB' :
                        u.role === 'gerant' ? '#E6F1FB' : '#EAF3DE'
                    }}>
                      {u.prenom?.[0]}{u.nom?.[0]}
                    </div>
                    <div>
                      <div className="user-nom">{u.prenom} {u.nom}</div>
                      {u.id === currentUser?.id && (
                        <div className="user-moi">← Vous</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="user-email">
                  {u.email}
                  {u.googleAutorise && (
                    <span title="Connexion Google autorisée" style={{
                      marginLeft: 6, fontSize: 10, fontWeight: 700,
                      background: '#E8F0FE', color: '#1A73E8',
                      padding: '2px 6px', borderRadius: 10,
                    }}>G</span>
                  )}
                </td>
                <td>{roleBadge(u.role)}</td>
                <td>
                  {u.boutiqueNom ? (
                    <span className="boutique-badge">🏪 {u.boutiqueNom}</span>
                  ) : u.role === 'vendeur' ? (
                    <span className="boutique-manquante">⚠️ Non assigné</span>
                  ) : (
                    <span style={{ color: '#888', fontSize: 12 }}>Toutes les boutiques</span>
                  )}
                </td>
                <td>
                  <span className={`statut-badge ${u.actif ? 'actif' : 'inactif'}`}>
                    {u.actif ? '✓ Actif' : '✗ Inactif'}
                  </span>
                </td>
                <td>
                  <div className="td-actions">
                    <button className="btn-action" onClick={() => ouvrirEdition(u)}>✏️</button>
                    <button
                      className={`btn-action ${u.actif ? 'btn-desactiver' : 'btn-activer'}`}
                      onClick={() => toggleActif(u)}
                      disabled={u.id === currentUser?.id}>
                      {u.actif ? '🔒' : '🔓'}
                    </button>
                    <button
                      className="btn-action btn-supprimer"
                      onClick={() => { setUserSelectionne(u); setShowDeleteModal(true); }}
                      disabled={u.id === currentUser?.id}>
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {utilisateurs.length === 0 && (
          <div className="table-empty">Aucun utilisateur trouvé</div>
        )}
      </div>

      {/* INFO RÔLES */}
      <div className="roles-info">
        <h3>Niveaux d'accès et boutiques</h3>
        <div className="roles-grid">
          <div className="role-card">
            <div className="role-icon">👑</div>
            <div className="role-nom">Admin</div>
            <div className="role-desc">
              Accès complet à toutes les boutiques et tous les modules.
              Pas de restriction par boutique.
            </div>
          </div>
          <div className="role-card">
            <div className="role-icon">🏪</div>
            <div className="role-nom">Gérant</div>
            <div className="role-desc">
              Accès à tous les modules sauf administration.
              Peut voir les données de toutes les boutiques.
            </div>
          </div>
          <div className="role-card">
            <div className="role-icon">🛒</div>
            <div className="role-nom">Vendeur</div>
            <div className="role-desc">
              Accès limité à la caisse, produits et clients.
              Doit être assigné à une boutique spécifique.
            </div>
          </div>
        </div>
      </div>

      {/* MODAL CREATION / EDITION */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{enEdition ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

              <div className="form-section">Informations personnelles</div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Prénom <span className="req">*</span></label>
                  <input value={form.prenom}
                    onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))}
                    placeholder="Ex: Fatou" />
                </div>
                <div className="form-group">
                  <label>Nom <span className="req">*</span></label>
                  <input value={form.nom}
                    onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                    placeholder="Ex: Diallo" />
                </div>
              </div>

              <div className="form-section">Accès</div>
              <div className="form-group">
                <label>Email <span className="req">*</span></label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="utilisateur@nexacom.bf"
                  disabled={!!enEdition}
                  style={{ background: enEdition ? '#F8F7F2' : '#fff' }}
                />
                {enEdition && (
                  <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                    L'email ne peut pas être modifié
                  </div>
                )}
              </div>

              {!enEdition && (
                <div className="form-group">
                  <label>Mot de passe <span className="req">*</span></label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Minimum 8 caractères"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Rôle</label>
                <div className="role-selector">
                  {[
                    { val: 'vendeur', label: '🛒 Vendeur', desc: 'Caisse & produits' },
                    { val: 'gerant', label: '🏪 Gérant', desc: 'Tous les modules' },
                    { val: 'admin', label: '👑 Admin', desc: 'Accès complet' },
                  ].map(r => (
                    <div key={r.val}
                      className={`role-option ${form.role === r.val ? 'active' : ''}`}
                      onClick={() => setForm(f => ({ ...f, role: r.val }))}>
                      <div className="ro-label">{r.label}</div>
                      <div className="ro-desc">{r.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* BOUTIQUE */}
              <div className="form-section">
                Boutique assignée
                {form.role === 'vendeur' && <span className="req"> *</span>}
              </div>

              {form.role === 'admin' || form.role === 'gerant' ? (
                <div className="boutique-info-box">
                  ℹ️ Les {form.role === 'admin' ? 'administrateurs' : 'gérants'} ont accès
                  à toutes les boutiques — aucune restriction nécessaire.
                </div>
              ) : (
                <div className="form-group">
                  <label>
                    Sélectionnez la boutique du vendeur <span className="req">*</span>
                  </label>
                  <select
                    value={form.boutiqueId || ''}
                    onChange={e => changerBoutique(e.target.value ? +e.target.value : null)}>
                    <option value="">— Choisir une boutique —</option>
                    {boutiques.map(b => (
                      <option key={b.id} value={b.id}>
                        🏪 {b.nom} — {b.ville}
                      </option>
                    ))}
                  </select>
                  {form.boutiqueId && (
                    <div className="boutique-selectionnee">
                      ✓ Ce vendeur sera limité à la <strong>{form.boutiqueNom}</strong>
                    </div>
                  )}
                </div>
              )}

              {enEdition && (
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="toggle-row">
                    <span>Compte actif</span>
                    <input type="checkbox" checked={form.actif}
                      onChange={e => setForm(f => ({ ...f, actif: e.target.checked }))} />
                  </label>
                </div>
              )}

              <div className="form-section">Connexion Google</div>
              <div className="form-group">
                <label className="toggle-row">
                  <span>Autoriser la connexion avec Google</span>
                  <input type="checkbox" checked={form.googleAutorise}
                    onChange={e => setForm(f => ({ ...f, googleAutorise: e.target.checked }))} />
                </label>
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                  Si activé, l'utilisateur pourra se connecter via son compte Google
                  (l'adresse Google doit être identique à l'email ci-dessus). Le mot de
                  passe reste utilisable.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={sauvegarder}>
                {enEdition ? 'Enregistrer' : "Créer l'utilisateur"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SUPPRESSION */}
      {showDeleteModal && userSelectionne && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header danger">
              <h2>⚠️ Supprimer l'utilisateur</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Voulez-vous supprimer <strong>
                {userSelectionne.prenom} {userSelectionne.nom}
              </strong> ? Cette action est irréversible.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Annuler
              </button>
              <button className="btn-danger" onClick={supprimer}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}